import { useEffect, useState } from "react";
import { Check, ChevronDown, Loader2, SlidersHorizontal } from "lucide-react";

// Admin menentukan jenis isian yang perlu dilengkapi pengelola pada sebuah
// item: Link Website / Foto / Dokumen / Teks Bebas / Pilihan — bisa dimatikan
// bila tidak relevan.
// - Teks Bebas: butuh "Pertanyaan/Perintah" (menjadi nama isian).
// - Pilihan: butuh "Pertanyaan/Perintah" + daftar opsi yang bisa dipilih.
const TYPES = [
  ["website", "Link Website"],
  ["foto", "Foto"],
  ["dokumen", "Dokumen"],
  ["teks", "Teks Bebas"],
  ["pilihan", "Pilihan"],
];

const draftOf = (item) => ({
  website: !!item.fields?.website?.enabled,
  foto: !!item.fields?.foto?.enabled,
  dokumen: !!item.fields?.dokumen?.enabled,
  teks: !!item.fields?.teks?.enabled,
  pilihan: !!item.fields?.pilihan?.enabled,
});

export default function FieldsDropdown({ item, busy, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftOf(item));
  const [prompt, setPrompt] = useState(item.fields?.teks?.prompt || "");
  const [pilPrompt, setPilPrompt] = useState(item.fields?.pilihan?.prompt || "");
  const [pilOptions, setPilOptions] = useState(
    (item.fields?.pilihan?.options || []).join("\n")
  );

  useEffect(() => {
    if (open) {
      setDraft(draftOf(item));
      setPrompt(item.fields?.teks?.prompt || "");
      setPilPrompt(item.fields?.pilihan?.prompt || "");
      setPilOptions((item.fields?.pilihan?.options || []).join("\n"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parsedOptions = pilOptions
    .split("\n")
    .map((o) => o.trim())
    .filter(Boolean);

  const anyOn = TYPES.some(([k]) => draft[k]);
  const savedOptions = item.fields?.pilihan?.options || [];
  const dirty =
    TYPES.some(([k]) => draft[k] !== !!item.fields?.[k]?.enabled) ||
    (draft.teks && (prompt || "").trim() !== (item.fields?.teks?.prompt || "")) ||
    (draft.pilihan &&
      ((pilPrompt || "").trim() !== (item.fields?.pilihan?.prompt || "") ||
        parsedOptions.join("\n") !== savedOptions.join("\n")));
  const waiting = busy.startsWith("fields-");
  const teksPromptMissing = draft.teks && !(prompt || "").trim();
  const pilPromptMissing = draft.pilihan && !(pilPrompt || "").trim();
  const pilOptionsMissing = draft.pilihan && parsedOptions.length === 0;
  const blocked =
    !anyOn || teksPromptMissing || pilPromptMissing || pilOptionsMissing;

  const savedLabel =
    TYPES.filter(([k]) => item.fields?.[k]?.enabled)
      .map(([, l]) => l)
      .join(", ") || "Tidak ada";

  const save = () => {
    if (blocked) return;
    onSave(
      {
        enabled: { ...draft },
        teksPrompt: (prompt || "").trim(),
        pilihanPrompt: (pilPrompt || "").trim(),
        pilihanOptions: parsedOptions,
      },
      () => setOpen(false)
    );
  };

  return (
    <div className="relative" data-testid={`monev-fields-${item.code}`}>
      <button
        type="button"
        data-testid={`monev-fields-trigger-${item.code}`}
        disabled={waiting}
        title="Jenis isian yang perlu dilengkapi pengelola"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex max-w-[240px] items-center gap-1.5 rounded-lg border border-[#E3EBF3] bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 outline-none transition-colors duration-200 hover:border-[#1479BF] hover:text-[#1479BF] disabled:opacity-50"
      >
        <SlidersHorizontal aria-hidden="true" className="h-3 w-3 shrink-0" />
        <span className="truncate" title={savedLabel}>
          {savedLabel}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-2 w-72 rounded-2xl border border-[#E3EBF3] bg-white p-3 shadow-xl">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              Jenis isian untuk pengelola
            </p>
            {TYPES.map(([k, lbl]) => (
              <div key={k}>
                <label className="mt-2 flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150 hover:bg-[#F3F7FB]">
                  <input
                    type="checkbox"
                    data-testid={`monev-fields-opt-${k}-${item.code}`}
                    checked={draft[k]}
                    onChange={() => setDraft((d) => ({ ...d, [k]: !d[k] }))}
                    className="h-4 w-4 accent-[#1479BF]"
                  />
                  <span className="text-xs font-semibold text-[#0B2440]">
                    {lbl}
                  </span>
                </label>

                {k === "teks" && draft.teks && (
                  <div className="mb-1 ml-2 mr-1">
                    <label className="text-[10px] font-bold text-slate-500">
                      Pertanyaan/Perintah:
                    </label>
                    <textarea
                      data-testid={`monev-fields-teks-prompt-${item.code}`}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      rows={2}
                      placeholder="mis. Tuliskan ringkasan kegiatan…"
                      className="mt-1 w-full rounded-lg border border-[#E3EBF3] px-2.5 py-1.5 text-xs outline-none transition focus:border-[#1479BF]"
                    />
                    {teksPromptMissing && (
                      <p className="mt-1 text-[10px] text-red-500">
                        Pertanyaan/Perintah wajib diisi.
                      </p>
                    )}
                  </div>
                )}

                {k === "pilihan" && draft.pilihan && (
                  <div className="mb-1 ml-2 mr-1">
                    <label className="text-[10px] font-bold text-slate-500">
                      Pertanyaan/Perintah:
                    </label>
                    <textarea
                      data-testid={`monev-fields-pilihan-prompt-${item.code}`}
                      value={pilPrompt}
                      onChange={(e) => setPilPrompt(e.target.value)}
                      rows={2}
                      placeholder="mis. Pilih status pelaksanaan…"
                      className="mt-1 w-full rounded-lg border border-[#E3EBF3] px-2.5 py-1.5 text-xs outline-none transition focus:border-[#1479BF]"
                    />
                    {pilPromptMissing && (
                      <p className="mt-1 text-[10px] text-red-500">
                        Pertanyaan/Perintah wajib diisi.
                      </p>
                    )}
                    <label className="mt-2 block text-[10px] font-bold text-slate-500">
                      Pilihan (satu per baris):
                    </label>
                    <textarea
                      data-testid={`monev-fields-pilihan-options-${item.code}`}
                      value={pilOptions}
                      onChange={(e) => setPilOptions(e.target.value)}
                      rows={3}
                      placeholder={"Sudah\nBelum\nDalam proses"}
                      className="mt-1 w-full rounded-lg border border-[#E3EBF3] px-2.5 py-1.5 text-xs outline-none transition focus:border-[#1479BF]"
                    />
                    {pilOptionsMissing && (
                      <p className="mt-1 text-[10px] text-red-500">
                        Tambahkan minimal satu opsi.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
            {!anyOn && (
              <p className="mt-2 text-[10px] leading-relaxed text-red-500">
                Minimal satu jenis isian harus aktif.
              </p>
            )}
            <button
              type="button"
              data-testid={`monev-fields-save-${item.code}`}
              disabled={!dirty || waiting || blocked}
              onClick={save}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#1479BF] px-3 py-2 text-[11px] font-bold text-white transition-colors duration-200 hover:bg-[#0B2440] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {waiting ? (
                <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              Simpan
            </button>
          </div>
        </>
      )}
    </div>
  );
}
